/**
 * Shopify Cart Rewards Refactored System (Concierge Upsell Engine)
 * Earthy, Premium, and Production-Grade implementation for Perishable Foods.
 * 
 * Features:
 * - Direct usage of existing Yogurt products & variants (No hidden products).
 * - Safe add/remove flavor transitions with line-item property tagging (_reward_type).
 * - Multi-flavor selector grid for State 2 (₹109 tier) and State 3 (Complimentary tier).
 * - Dynamic cheese dip add-on recommendations for subtotal near-unlock (₹650–₹799).
 * - Near-free-gift threshold nudging (₹900–₹999).
 * - Anti-loop sequential AJAX call execution.
 * - Performance Optimized: 150ms debounced updates, global event delegation, DOM comparison buffers.
 */

(function () {
  'use strict';

  // Single-Instance Initialization Shield (Prevents duplicate engines and overlapping handlers)
  if (window.ShopifyCartRewardsInitialized) {
    console.warn('[CartRewards] System already initialized. Skipping duplicate initialization.');
    return;
  }
  window.ShopifyCartRewardsInitialized = true;

  // Config constants
  const YOGURT_HANDLES = [
    'yogut-blueberry-favour-greek-yogurt',
    'plain-greek-yogurt',
    'strawberry-greek-yogurt',
    'orange-greek-yogurt'
  ];

  const ADDONS_CONFIG = [
    {
      handle: 'sour-cream-onion-dip',
      title: 'Sour Cream & Onion Dip',
      image: 'https://perishablefoods.lovable.app/images/sour-cream-onion-cheese-dip.png',
      price: 15000,
      fallbackVariantId: 44200000000021
    },
    {
      handle: 'thai-chilli-dip',
      title: 'Thai Chilli Dip',
      image: 'https://perishablefoods.lovable.app/images/thai-chilli-cheese-dip.png',
      price: 15000,
      fallbackVariantId: 44200000000022
    },
    {
      handle: 'mexican-jalapeno-dip',
      title: 'Mexican Jalapeno Dip',
      image: 'https://perishablefoods.lovable.app/images/meexican-jalapeno-cheese-dip.png',
      price: 15000,
      fallbackVariantId: 44200000000023
    }
  ];

  const CONFIG = {
    thresholdDiscounted: 79900,      // ₹799
    thresholdComplimentary: 99900,   // ₹999
    nudgeAddonMin: 65000,            // ₹650 (Show add-ons below ₹799)
    nudgeComplimentaryMin: 90000,    // ₹900 (Show near-free nudge below ₹999)
    discountedRewardPrice: 10900     // ₹109 in cents
  };

  // State cache, locks, and DOM comparison buffers
  let isUpdating = false;
  let cachedSystemData = null;
  let addonsDismissed = false;

  // Local pointer for delegated event handlers
  const currentUIState = {
    activeRewardItem: null,
    type: '',
    refreshCartCallback: null
  };

  // HTML buffers to prevent layout thrashing and redundant DOM writes
  let lastProgressHtml = '';
  let lastAddonsHtml = '';
  let lastSelectorHtml = '';
  let lastBundleHtml = '';

  /**
   * Helper to format money into standard INR format
   */
  function formatINR(cents) {
    return '₹' + Math.max(0, Math.floor(cents / 100)).toLocaleString('en-IN');
  }

  /**
   * Safe Fallback Variant ID generators for offline/draft themes
   */
  function getFallbackVariantId(handle) {
    const map = {
      'yogut-blueberry-favour-greek-yogurt': 44200000000011,
      'plain-greek-yogurt': 44200000000012,
      'strawberry-greek-yogurt': 44200000000013,
      'orange-greek-yogurt': 44200000000014
    };
    return map[handle] || 44200000000000;
  }

  /**
   * Loads both Yogurts and Add-on signature dips metadata dynamically from
   * Shopify's Storefront API in parallel. Caches them for high performance.
   */
  async function loadSystemMetadata() {
    if (cachedSystemData) return cachedSystemData;

    try {
      const yogurtsPromise = YOGURT_HANDLES.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            const data = await res.json();
            return {
              handle,
              title: data.title.replace(/greek yogurt/gi, 'Greek Yogut®'),
              image: data.featured_image || 'https://images.unsplash.com/photo-1488477181946-6428a0291777?q=80&w=300&auto=format&fit=crop',
              variantId: data.variants?.[0]?.id || getFallbackVariantId(handle),
              price: data.price
            };
          }
        } catch (e) {
          console.warn(`[CartRewards] Failed to fetch Yogurt: ${handle}`, e);
        }
        return {
          handle,
          title: handle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?q=80&w=300&auto=format&fit=crop',
          variantId: getFallbackVariantId(handle),
          price: 15000
        };
      });

      const addonsPromise = ADDONS_CONFIG.map(async (config) => {
        try {
          const res = await fetch(`/products/${config.handle}.js`);
          if (res.ok) {
            const data = await res.json();
            return {
              handle: config.handle,
              title: data.title,
              image: data.featured_image || config.image,
              variantId: data.variants?.[0]?.id || config.fallbackVariantId,
              price: data.price
            };
          }
        } catch (e) {
          console.warn(`[CartRewards] Failed to fetch Add-on: ${config.handle}`, e);
        }
        return {
          handle: config.handle,
          title: config.title,
          image: config.image,
          variantId: config.fallbackVariantId,
          price: config.price
        };
      });

      const [yogurts, addons] = await Promise.all([
        Promise.all(yogurtsPromise),
        Promise.all(addonsPromise)
      ]);

      cachedSystemData = { yogurts, addons };
      return cachedSystemData;

    } catch (err) {
      console.error('[CartRewards] Failed to initialize system metadata:', err);
      // Absolute fallback if everything fails
      cachedSystemData = {
        yogurts: YOGURT_HANDLES.map(h => ({
          handle: h,
          title: h.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?q=80&w=300&auto=format&fit=crop',
          variantId: getFallbackVariantId(h),
          price: 15000
        })),
        addons: ADDONS_CONFIG.map(a => ({
          handle: a.handle,
          title: a.title,
          image: a.image,
          variantId: a.fallbackVariantId,
          price: a.price
        }))
      };
      return cachedSystemData;
    }
  }

  /**
   * Helper to perform standard lock-guarded cart operations
   */
  async function safeCartRequest(url, data, onComplete) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`Cart API call failed with status ${response.status}`);
      }
      if (typeof onComplete === 'function') {
        await onComplete();
      }
    } catch (err) {
      console.error('[CartRewards] Safe request failed:', err);
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Handles swapping reward variants inside the cart. 
   * Removes the old variant and adds the new variant sequentially, then refreshes the cart once.
   */
  async function swapRewardVariant(oldKey, newVariantId, type, refreshCartCallback) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      // Step 1: Remove old item
      if (oldKey) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: oldKey, quantity: 0 })
        });
      }

      // Step 2: Add new reward item with properties
      const rewardName = type === 'discounted' ? '₹109 Yogut Unlock' : 'Complimentary Yogut';
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity: 1,
            properties: {
              '_reward_type': type,
              'Reward': rewardName
            }
          }]
        })
      });

      // Step 3: Call final render refresh
      await refreshCartCallback();
    } catch (err) {
      console.error('[CartRewards] Error swapping reward variants:', err);
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Adds a standard cheese dip recommendation to the cart
   */
  async function addStandardProduct(variantId, refreshCartCallback) {
    if (isUpdating) return;
    isUpdating = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      await refreshCartCallback();
    } catch (err) {
      console.error('[CartRewards] Failed to add standard upsell product:', err);
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Main Cart Evaluator. Runs dynamically upon cart updates.
   */
  async function evaluateCartState(cart, refreshCartCallback, isServerConfirmed = false) {
    const meta = await loadSystemMetadata();

    // 1. Scan cart for ALL current reward indicators (Anti-stacking scanning)
    const discountedItems = cart.items.filter(item => item.properties && item.properties._reward_type === 'discounted');
    const complimentaryItems = cart.items.filter(item => item.properties && item.properties._reward_type === 'complimentary');

    // --- Anti-Exploit Sanitization Layer ---
    
    // Rule 1: Reset quantity of any reward item if quantity > 1 (No multi-unit reward hacking)
    const qtyViolator = [...discountedItems, ...complimentaryItems].find(item => item.quantity > 1);
    if (qtyViolator) {
      if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
      if (!isUpdating) {
        console.warn(`[CartRewards] Exploit detected: Reward variant quantity exceeds 1 (found ${qtyViolator.quantity}). Resetting quantity to 1 defensively.`);
        await safeCartRequest('/cart/change.js', { id: qtyViolator.key, quantity: 1 }, refreshCartCallback);
        return;
      }
    }

    // Rule 2: Clean up duplicate reward items of the same type (Anti-stacking cleanse)
    if (discountedItems.length > 1) {
      if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
      if (!isUpdating) {
        console.warn(`[CartRewards] Exploit detected: Multiple discounted reward variants found. Cleansing duplicates.`);
        const updates = {};
        discountedItems.slice(1).forEach(item => {
          updates[item.key] = 0;
        });
        await safeCartRequest('/cart/update.js', { updates }, refreshCartCallback);
        return;
      }
    }

    if (complimentaryItems.length > 1) {
      if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
      if (!isUpdating) {
        console.warn(`[CartRewards] Exploit detected: Multiple complimentary reward variants found. Cleansing duplicates.`);
        const updates = {};
        complimentaryItems.slice(1).forEach(item => {
          updates[item.key] = 0;
        });
        await safeCartRequest('/cart/update.js', { updates }, refreshCartCallback);
        return;
      }
    }

    // Single active reward reference markers
    const discountedItem = discountedItems[0] || null;
    const complimentaryItem = complimentaryItems[0] || null;

    // Calculate clean totals (excluding active reward items)
    let cleanSubtotal = cart.total_price;
    let rewardQty = 0;
    cart.items.forEach(item => {
      if (item.properties && (item.properties._reward_type === 'discounted' || item.properties._reward_type === 'complimentary')) {
        cleanSubtotal -= item.final_line_price;
        rewardQty += item.quantity;
      }
    });

    const cleanItemCount = cart.item_count - rewardQty;

    // Define states
    const isState1 = cleanSubtotal < CONFIG.thresholdDiscounted;
    const isState2 = cleanSubtotal >= CONFIG.thresholdDiscounted && cleanSubtotal < CONFIG.thresholdComplimentary;
    const isState3 = cleanSubtotal >= CONFIG.thresholdComplimentary;
    
    // Curated Bundle discount becomes inactive once subtotal reaches ₹799 milestone (State 2 or State 3)
    const isState4 = cleanItemCount >= 3 && cleanSubtotal < CONFIG.thresholdDiscounted;

    // --- State-Machine Action Boundary ---
    if (isState3) {
      // Must clean up any ₹109 Yogurt to prevent double-charging
      if (discountedItem) {
        if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
        if (!isUpdating) {
          await safeCartRequest('/cart/change.js', { id: discountedItem.key, quantity: 0 }, refreshCartCallback);
          return;
        }
      }
      
      // Auto-add standard Blueberry Yogurt as default complimentary gift if none selected yet
      if (!complimentaryItem) {
        if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
        if (!isUpdating) {
          const defaultYog = meta.yogurts.find(y => y.handle === 'yogut-blueberry-favour-greek-yogurt') || meta.yogurts[0];
          await safeCartRequest('/cart/add.js', {
            items: [{
              id: defaultYog.variantId,
              quantity: 1,
              properties: {
                '_reward_type': 'complimentary',
                'Reward': 'Complimentary Yogut'
              }
            }]
          }, refreshCartCallback);
          return;
        }
      }
    } else if (isState2) {
      // Must clean up any complimentary reward items
      if (complimentaryItem) {
        if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
        if (!isUpdating) {
          await safeCartRequest('/cart/change.js', { id: complimentaryItem.key, quantity: 0 }, refreshCartCallback);
          return;
        }
      }
    } else if (isState1) {
      // Below ₹799: Wipe all active rewards
      if (discountedItem || complimentaryItem) {
        if (!isServerConfirmed) return; // Wait for server confirmation before modifying cart
        if (!isUpdating) {
          const updates = {};
          if (discountedItem) updates[discountedItem.key] = 0;
          if (complimentaryItem) updates[complimentaryItem.key] = 0;
          await safeCartRequest('/cart/update.js', { updates }, refreshCartCallback);
          return;
        }
      }
    }

    // --- UI Render Phase ---
    renderConciergeUI(cleanSubtotal, cleanItemCount, {
      isState1,
      isState2,
      isState3,
      isState4,
      discountedItem,
      complimentaryItem,
      meta,
      refreshCartCallback
    });
  }

  /**
   * Premium Luxury Concierge Drawer UI Generator with HTML Buffers
   */
  function renderConciergeUI(subtotal, quantity, state) {
    const cartBody = document.getElementById('pf-cart-body');
    let container = document.getElementById('pf-cart-rewards-container');
    if (!container) {
      const drawer = document.getElementById('pf-cart-drawer');
      if (!drawer) return;
      container = document.createElement('div');
      container.id = 'pf-cart-rewards-container';
      const header = drawer.querySelector('.pf-cart-drawer__header');
      if (header) {
        header.insertAdjacentElement('afterend', container);
      } else {
        drawer.prepend(container);
      }
    }

    // Update modular local UI state for delegated event handlers
    currentUIState.activeRewardItem = state.isState2 ? state.discountedItem : state.complimentaryItem;
    currentUIState.type = state.isState2 ? 'discounted' : 'complimentary';
    currentUIState.refreshCartCallback = state.refreshCartCallback;

    // 1. Sticky Progress HTML Generation
    let progressPercent = 0;
    let progressLabel = '';
    let discountMsg = '';

    if (state.isState2) {
      if (state.discountedItem) {
        const discountAmount = state.discountedItem.price - CONFIG.discountedRewardPrice;
        discountMsg = ` (saving <span class="rewards-accent">${formatINR(discountAmount)}</span>)`;
      } else {
        discountMsg = ` (save <span class="rewards-accent">₹41</span>)`;
      }
    } else if (state.isState3) {
      if (state.complimentaryItem) {
        discountMsg = ` (saving <span class="rewards-accent">${formatINR(state.complimentaryItem.price)}</span>)`;
      } else {
        discountMsg = ` (save <span class="rewards-accent">₹150</span>)`;
      }
    }

    if (state.isState1) {
      const remaining = CONFIG.thresholdDiscounted - subtotal;
      progressPercent = (subtotal / CONFIG.thresholdComplimentary) * 100;

      if (subtotal >= CONFIG.nudgeAddonMin) {
        progressLabel = `Curating your concierge benefits. You're only <span class="rewards-accent">${formatINR(remaining)}</span> away from unlocking <strong class="font-serif">Fresh Yogut®</strong> at <span class="rewards-accent">₹109</span>!`;
      } else {
        progressLabel = `Add <span class="rewards-accent">${formatINR(remaining)}</span> to unlock <strong class="font-serif">Fresh Yogut®</strong> at a special <span class="rewards-accent">₹109</span> pricing.`;
      }
    } else if (state.isState2) {
      const remaining = CONFIG.thresholdComplimentary - subtotal;
      progressPercent = (subtotal / CONFIG.thresholdComplimentary) * 100;

      if (subtotal >= CONFIG.nudgeComplimentaryMin) {
        progressLabel = `✨ Outstanding! You're only <span class="rewards-accent">${formatINR(remaining)}</span> away from a <strong class="font-serif">complimentary Yogurt®</strong>!`;
      } else {
        progressLabel = `You've unlocked <strong class="font-serif">Fresh Yogut®</strong> at <span class="rewards-accent">₹109</span>${discountMsg}! Select flavor below, or add <span class="rewards-accent">${formatINR(remaining)}</span> for a free gift.`;
      }
    } else if (state.isState3) {
      progressPercent = 100;
      progressLabel = `🏆 <strong class="font-serif">Complimentary Yogut®</strong> fully unlocked${discountMsg}! Select your flavor below.`;
    }

    let bundleNudgeHtml = '';
    if (subtotal < CONFIG.thresholdDiscounted) {
      if (quantity < 3) {
        const remaining = 3 - quantity;
        bundleNudgeHtml = `<div class="pf-rewards-bundle-nudge">Add ${remaining} more product${remaining > 1 ? 's' : ''} to the cart to experience a flat 8% off!</div>`;
      } else {
        bundleNudgeHtml = `<div class="pf-rewards-bundle-nudge unlocked">🎉 Curated Bundle active: You are enjoying a flat 8% off!</div>`;
      }
    }

    const currentProgressHtml = `
      <div class="pf-rewards-progress-wrapper concierge-style">
        <div class="pf-rewards-status">${progressLabel}</div>
        ${bundleNudgeHtml}
        <div class="pf-rewards-bar-track">
          <div class="pf-rewards-bar-fill" style="width: ${progressPercent}%">
            <span class="pf-rewards-shimmer"></span>
          </div>
          <div class="pf-rewards-marker marker-discounted ${subtotal >= CONFIG.thresholdDiscounted ? 'unlocked' : ''}" style="left: ${(CONFIG.thresholdDiscounted / CONFIG.thresholdComplimentary) * 100}%">
            <span class="marker-dot"></span>
            <span class="marker-label">₹109</span>
          </div>
          <div class="pf-rewards-marker marker-free ${subtotal >= CONFIG.thresholdComplimentary ? 'unlocked' : ''}" style="left: 100%">
            <span class="marker-dot"></span>
            <span class="marker-label">Free</span>
          </div>
        </div>
      </div>
    `;

    // Only update innerHTML if progress bar content has changed (Prevents thrashing)
    if (currentProgressHtml !== lastProgressHtml) {
      container.innerHTML = currentProgressHtml;
      lastProgressHtml = currentProgressHtml;
      // Wiping secondary buffers since the main container innerHTML was overwritten
      lastBundleHtml = '';
      lastAddonsHtml = '';
    }

    // Proactively clean up and remove any Curated Bundle Banner if it exists in the DOM
    let bundleEl = container.querySelector('.pf-rewards-bundle-banner');
    if (bundleEl) {
      bundleEl.remove();
      lastBundleHtml = '';
    }

    // Reset dismissal when cart goes out of the nudge zone
    if (subtotal < CONFIG.nudgeAddonMin || subtotal >= CONFIG.thresholdDiscounted) {
      addonsDismissed = false;
    }

    // 3. Dynamic Add-on Suggestions HTML Generation (State 1 between ₹650–₹799)
    let currentAddonsHtml = '';
    const shouldShowAddons = state.isState1 && subtotal >= CONFIG.nudgeAddonMin && subtotal < CONFIG.thresholdDiscounted && !addonsDismissed;

    if (shouldShowAddons) {
      currentAddonsHtml = `
        <button class="addons-close-btn" data-action="dismiss-addons" title="Dismiss recommendations">✕</button>
        <div class="addons-heading font-serif">Signature Cheese Pairings</div>
        <div class="addons-subtitle">Add a small-batch cheese dip to instantly unlock your ₹109 Yogurt reward:</div>
        <div class="addons-list">
          ${state.meta.addons.map(addon => `
            <div class="addon-item">
              <img src="${addon.image}" alt="${addon.title}" class="addon-item__img">
              <div class="addon-item__info">
                <div class="addon-item__title">${addon.title}</div>
                <div class="addon-item__price">${formatINR(addon.price)}</div>
              </div>
              <button class="addon-item__btn" data-action="add-addon" data-variant-id="${addon.variantId}">
                Add
              </button>
            </div>
          `).join('')}
        </div>
      `;
    }

    let addonsEl = container.querySelector('.pf-rewards-addons-wrapper');
    if (currentAddonsHtml) {
      if (!addonsEl) {
        addonsEl = document.createElement('div');
        addonsEl.className = 'pf-rewards-addons-wrapper';
        container.appendChild(addonsEl);
      }
      if (currentAddonsHtml !== lastAddonsHtml) {
        addonsEl.innerHTML = currentAddonsHtml;
        lastAddonsHtml = currentAddonsHtml;
      }
      if (cartBody) {
        cartBody.style.setProperty('overflow-y', 'hidden', 'important');
      }
    } else {
      if (addonsEl) {
        addonsEl.remove();
        lastAddonsHtml = '';
      }
      if (cartBody) {
        cartBody.style.removeProperty('overflow-y');
      }
    }

    // 4. Flavor Selector HTML Generation (State 2 and State 3)
    if (!cartBody) return;

    let currentSelectorHtml = '';
    const shouldShowSelector = state.isState2 || state.isState3;

    if (shouldShowSelector) {
      const isDiscountedMode = state.isState2;
      const activeRewardItem = isDiscountedMode ? state.discountedItem : state.complimentaryItem;
      const currentClaimedVariantId = activeRewardItem ? activeRewardItem.variant_id : null;

      const headerTitle = isDiscountedMode ? "Customize Your ₹109 Yogurt Benefit" : "Select Your Complimentary Gift Flavor";
      const headerSubtitle = isDiscountedMode ? "Select one gourmet flavor to unlock at ₹109:" : "Enjoy any fresh yogurt flavor completely free:";

      const filteredYogurts = isDiscountedMode
        ? state.meta.yogurts.filter(yogut => yogut.handle !== 'plain-greek-yogurt')
        : state.meta.yogurts;

      currentSelectorHtml = `
        <div class="pf-concierge-selector">
          <div class="selector-header">
            <h4 class="selector-title font-serif">${headerTitle}</h4>
            <p class="selector-subtitle">${headerSubtitle}</p>
          </div>
          <div class="flavor-grid">
            ${filteredYogurts.map(yogut => {
        const isSelected = currentClaimedVariantId === yogut.variantId;
        const buttonText = isSelected
          ? 'Selected'
          : (isDiscountedMode ? 'Claim ₹109' : 'Claim Free');

        return `
                <div class="flavor-card ${isSelected ? 'selected' : ''}">
                  <img src="${yogut.image}" alt="${yogut.title}" class="flavor-card__img">
                  <div class="flavor-card__info">
                    <div class="flavor-card__title">${yogut.title}</div>
                    <div class="flavor-card__price-row">
                      <span class="flavor-card__new-price">${isDiscountedMode ? '₹109' : 'FREE'}</span>
                      <span class="flavor-card__old-price">${formatINR(yogut.price)}</span>
                    </div>
                  </div>
                  <button class="flavor-card__btn ${isSelected ? 'active' : ''}" 
                          data-action="claim-flavor" 
                          data-variant-id="${yogut.variantId}"
                          ${isSelected ? 'disabled' : ''}>
                    ${buttonText}
                  </button>
                </div>
              `;
      }).join('')}
          </div>
        </div>
      `;
    }

    let selectorEl = cartBody.querySelector('.pf-concierge-selector-wrapper');
    if (currentSelectorHtml) {
      if (!selectorEl) {
        selectorEl = document.createElement('div');
        selectorEl.className = 'pf-concierge-selector-wrapper';
        cartBody.appendChild(selectorEl);
      }
      if (currentSelectorHtml !== lastSelectorHtml) {
        selectorEl.innerHTML = currentSelectorHtml;
        lastSelectorHtml = currentSelectorHtml;
      }
    } else if (selectorEl) {
      selectorEl.remove();
      lastSelectorHtml = '';
    }
  }

  // Global Event Delegation click listener on document.body (Zero memory leaks, attaches exactly once)
  document.body.addEventListener('click', async (e) => {
    // 1. Claim flavor click
    const claimBtn = e.target.closest('[data-action="claim-flavor"]');
    if (claimBtn) {
      e.preventDefault();
      if (isUpdating) return;

      const variantId = parseInt(claimBtn.dataset.variantId);
      const refreshCallback = currentUIState.refreshCartCallback;
      const oldKey = currentUIState.activeRewardItem ? currentUIState.activeRewardItem.key : null;
      const type = currentUIState.type;

      if (!refreshCallback) return;

      claimBtn.textContent = 'Claiming...';
      claimBtn.disabled = true;
      await swapRewardVariant(oldKey, variantId, type, refreshCallback);
      return;
    }

    // 2. Add signature dip add-on click
    const addonBtn = e.target.closest('[data-action="add-addon"]');
    if (addonBtn) {
      e.preventDefault();
      if (isUpdating) return;

      const variantId = parseInt(addonBtn.dataset.variantId);
      const refreshCallback = currentUIState.refreshCartCallback;

      if (!refreshCallback) return;

      addonBtn.textContent = '...';
      addonBtn.disabled = true;
      await addStandardProduct(variantId, refreshCallback);
      return;
    }

    // 3. Dismiss cheese pairings click
    const dismissBtn = e.target.closest('[data-action="dismiss-addons"]');
    if (dismissBtn) {
      e.preventDefault();
      addonsDismissed = true;
      const addonsEl = document.querySelector('.pf-rewards-addons-wrapper');
      if (addonsEl) {
        // Smooth slide-up fade out animation
        addonsEl.style.transition = 'all 0.4s ease';
        addonsEl.style.opacity = '0';
        addonsEl.style.transform = 'translateY(-10px)';
        addonsEl.style.maxHeight = '0';
        addonsEl.style.padding = '0';
        addonsEl.style.margin = '0';
        setTimeout(() => {
          addonsEl.remove();
          lastAddonsHtml = ''; // Clear buffer to prevent unwanted redraw
        }, 400);
      }
      return;
    }
  });

  // Global API attachment
  window.ShopifyCartRewards = {
    CONFIG,
    update: function (cart, refreshCartCallback, isServerConfirmed = false) {
      if (!cart) return;

      evaluateCartState(cart, refreshCartCallback, isServerConfirmed).catch(err => {
        console.error('[CartRewards] Evaluation failed gracefully:', err);
      });
    }
  };

})();
